import { z } from 'zod'

import {
  ANALYTICS_DOCUMENT_ENTITIES,
  ANALYTICS_ENTITIES,
  ANALYTICS_ONBOARDING_STEPS,
  ANALYTICS_REPORT_TABS,
} from './analytics.constants'

const RoutePatternSchema = z
  .string()
  .max(200)
  .regex(/^\/[A-Za-z0-9_$.\-/]*$/)

const CodeIdentifierSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9._-]{0,63}$/)

const EntitySchema = z.enum(ANALYTICS_ENTITIES)
const DocumentEntitySchema = z.enum(ANALYTICS_DOCUMENT_ENTITIES)
const FeedbackRatingSchema = z.number().int().min(1).max(5)

export const ANALYTICS_EVENTS = {
  page_view: { route_pattern: RoutePatternSchema },
  onboarding_step_viewed: { step: z.enum(ANALYTICS_ONBOARDING_STEPS) },
  onboarding_step_completed: { step: z.enum(ANALYTICS_ONBOARDING_STEPS) },
  onboarding_completed: {},
  create_started: { entity: EntitySchema },
  client_created: {},
  quote_created: {},
  quote_sent: {},
  quote_converted: { split: z.boolean() },
  invoice_created: {},
  invoice_sent: {},
  invoice_payment_recorded: {},
  invoice_reminder_sent: {},
  recurring_invoice_created: {},
  expense_created: {},
  pdf_downloaded: { entity: DocumentEntitySchema },
  report_viewed: { report_tab: z.enum(ANALYTICS_REPORT_TABS) },
  data_import_completed: {},
  data_export_requested: { export_type: CodeIdentifierSchema },
  form_validation_failed: { form_id: EntitySchema, field: CodeIdentifierSchema },
  form_submit_failed: { form_id: EntitySchema },
  pwa_install_prompt_shown: {},
  pwa_install_accepted: {},
  pwa_install_dismissed: {},
  feedback_opened: {},
  feedback_submitted: { category: CodeIdentifierSchema, rating: FeedbackRatingSchema },
} as const satisfies Record<string, z.ZodRawShape>

export type AnalyticsEventName = keyof typeof ANALYTICS_EVENTS

export type AnalyticsEventParams<EventName extends AnalyticsEventName> = z.infer<
  z.ZodObject<(typeof ANALYTICS_EVENTS)[EventName]>
>

export const ANALYTICS_KEY_EVENTS = [
  'onboarding_completed',
  'quote_sent',
  'invoice_sent',
  'invoice_payment_recorded',
] as const satisfies readonly AnalyticsEventName[]

export function parseAnalyticsEvent<EventName extends AnalyticsEventName>(
  eventName: EventName,
  params: unknown,
) {
  return z.strictObject(ANALYTICS_EVENTS[eventName]).safeParse(params)
}
